import { useEditor } from '../../state/editorStore';
import { playbackClock } from '../../playback/clock';
import { HOOK_STYLE_PRESETS, readableTextColor, type HookMoment } from '@captionflow/caption-engine';

/**
 * Hooks / Takeovers tab (LEFT) - the RARE (≤4/video) big center-screen power-word
 * moments. This panel is the LIST + a style-PRESET grid (like the caption Styles
 * tab). Selecting a hook opens the fine-tune controls in the right inspector
 * (StudioHookInspector). A takeover has its OWN time window and can hide captions.
 */
function secs(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}

type Patch = (typeof HOOK_STYLE_PRESETS)[number]['patch'];

/** Approximate match so the current look's preset chip reads as selected. */
function matches(h: HookMoment, patch: Patch): boolean {
  return (Object.entries(patch) as [keyof Patch, unknown][]).every(([k, v]) => {
    const hv = (h as unknown as Record<string, unknown>)[k];
    if (v === undefined) return hv == null; // a cleared field
    if (k === 'fill') return (hv ?? 'solid') === v;
    if (typeof v === 'object') return JSON.stringify(hv) === JSON.stringify(v);
    return hv === v;
  });
}

/** Inline style approximating a preset's look for its swatch. */
function swatchStyle(patch: Patch): React.CSSProperties {
  const s: React.CSSProperties = { color: '#fff' };
  if (patch.bgColor) {
    s.background = patch.bgColor;
    s.color = readableTextColor(patch.bgColor);
    s.padding = '2px 7px';
    s.borderRadius = '6px';
  } else if (patch.gradient) {
    s.background = `linear-gradient(${patch.gradient.angleDeg}deg, ${patch.gradient.from}, ${patch.gradient.to})`;
    s.WebkitBackgroundClip = 'text';
    s.backgroundClip = 'text';
    s.color = 'transparent';
  } else {
    s.color = patch.color && patch.color !== 'transparent' ? patch.color : '#fff';
  }
  if (patch.color === 'transparent') {
    s.color = 'transparent';
    s.WebkitTextStroke = `1px ${patch.stroke?.color ?? '#fff'}`;
  } else if (patch.stroke) {
    s.WebkitTextStroke = `1px ${patch.stroke.color}`;
  }
  if (patch.glow) s.textShadow = `0 0 7px ${patch.glow.color}, 0 0 12px ${patch.glow.color}`;
  if (patch.fill === 'metal') {
    const c = patch.color ?? '#d9a520';
    s.background = `linear-gradient(180deg, #fff6d6 0%, ${c} 45%, #6b4a08 52%, ${c} 64%, #8a6410 100%)`;
    s.WebkitBackgroundClip = 'text';
    s.backgroundClip = 'text';
    s.color = 'transparent';
  }
  if (patch.fill === 'footage') {
    // Stand-in for "the video inside the letters".
    const tint = patch.texture?.tint && (patch.texture.tintAmount ?? 0) > 0.3 ? patch.texture.tint : null;
    s.background = tint
      ? `linear-gradient(135deg, ${tint}, #fff8b0 40%, ${tint} 70%, #9c8a2a)`
      : 'linear-gradient(135deg, #7aa7d8, #e8dccb 35%, #6d8c4a 60%, #b7c9e2)';
    s.WebkitBackgroundClip = 'text';
    s.backgroundClip = 'text';
    s.color = 'transparent';
  }
  if (patch.fontFamily) s.fontFamily = patch.fontFamily;
  if (patch.fontWeight) s.fontWeight = patch.fontWeight;
  if (patch.italic) s.fontStyle = 'italic';
  if (patch.textCase === 'asis') s.textTransform = 'none';
  return s;
}

/** Two-line sample for the multi-line looks. */
function PresetSample({ patch }: { patch: Patch }) {
  const base = swatchStyle(patch);
  if (!patch.lineStyles?.length) return <span className="hkp-sample" style={base}>Hook</span>;
  const words = ['Big', 'idea'];
  return (
    <span className="hkp-sample hkp-stack" style={{ textAlign: patch.align ?? 'center' }}>
      {words.map((w, i) => {
        const ls = patch.lineStyles?.[i] ?? null;
        const st: React.CSSProperties = { ...base, display: 'block' };
        if (ls?.fontFamily) st.fontFamily = ls.fontFamily;
        if (ls?.fontWeight) st.fontWeight = ls.fontWeight;
        if (ls?.italic) st.fontStyle = 'italic';
        if (ls?.size) st.fontSize = `${Math.round(17 * Math.min(1.4, Math.max(0.55, ls.size)))}px`;
        if (ls?.color) {
          st.color = ls.color;
          st.background = 'none';
        }
        return <span key={w} style={st}>{w}</span>;
      })}
    </span>
  );
}

export function StudioHookMomentPanel() {
  const hooks = useEditor((s) => s.hooks);
  const words = useEditor((s) => s.words);
  const durationMs = useEditor((s) => s.durationMs);
  const selectedWordId = useEditor((s) => s.selectedWordId);
  const selectedId = useEditor((s) => s.selectedHookId);
  const addHook = useEditor((s) => s.addHook);
  const updateHook = useEditor((s) => s.updateHook);
  const removeHook = useEditor((s) => s.removeHook);
  const selectHook = useEditor((s) => s.selectHook);
  const cutout = useEditor((s) => s.cutout);
  const setDepthDialogOpen = useEditor((s) => s.setDepthDialogOpen);

  const dur = Math.max(durationMs, hooks.at(-1)?.endMs ?? 0, 1000);
  const selected = hooks.find((h) => h.id === selectedId) ?? null;

  const add = () => {
    const now = playbackClock.timeMs();
    const word = words.find((w) => w.id === selectedWordId);
    const id = addHook({
      startMs: now,
      endMs: Math.min(dur, now + 1800),
      text: word ? word.text : 'HOOK',
      anchorWordId: word?.id,
    });
    selectHook(id);
  };

  const applyPreset = (patch: Patch) => {
    if (!selected) return;
    // Keep the user's per-line TIMING + strikes when swapping looks.
    const keep = (i: number) => {
      const old = selected.lineStyles?.[i];
      return old ? { delayMs: old.delayMs, strike: old.strike } : {};
    };
    const n = Math.max(patch.lineStyles?.length ?? 0, selected.lineStyles?.length ?? 0);
    const lineStyles = n
      ? Array.from({ length: n }, (_, i) => {
          const merged = Object.fromEntries(
            Object.entries({ ...(patch.lineStyles?.[i] ?? {}), ...keep(i) }).filter(([, v]) => v !== undefined),
          );
          return Object.keys(merged).length ? merged : null;
        })
      : undefined;
    updateHook(selected.id, { ...patch, lineStyles });
    // A preset can turn on a behind look? (none do today) - keep parity with the
    // per-hook behind toggle just in case a future preset sets it.
    if ((patch as { behind?: boolean }).behind && cutout.status !== 'ready') setDepthDialogOpen(true);
  };

  return (
    <div className="slp-body shk">
      <button className="sau-add" onClick={add}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8Z" />
        </svg>
        Add hook at playhead
      </button>

      <span className="str-section">On timeline{hooks.length ? ` · ${hooks.length}` : ''}</span>
      {hooks.length === 0 ? (
        <div className="str-empty">No hooks yet. Add one above.</div>
      ) : (
        <div className="str-list">
          {hooks.map((h) => (
            <button
              key={h.id}
              className={h.id === selectedId ? 'str-item-head on' : 'str-item-head'}
              onClick={() => { selectHook(h.id); playbackClock.seek(h.startMs); }}
            >
              <span className="brl-badge">{h.type === 'opening' ? '★' : '⚡'}</span>
              <span className="str-item-name">{h.text.split('\n').filter(Boolean).join(' / ') || '(empty)'}</span>
              <span className="str-item-time">{secs(h.startMs)} → {secs(h.endMs)}</span>
              <span className="str-remove-x" role="button" aria-label="Remove"
                onClick={(e) => { e.stopPropagation(); removeHook(h.id); }}>✕</span>
            </button>
          ))}
        </div>
      )}

      {selected ? (
        <>
          <span className="str-section">Style presets</span>
          <div className="hkp-grid">
            {HOOK_STYLE_PRESETS.map((p) => (
              <button key={p.id} className={matches(selected, p.patch) ? 'hkp on' : 'hkp'}
                onClick={() => applyPreset(p.patch)} title={p.desc}>
                <PresetSample patch={p.patch} />
                <span className="hkp-name">{p.name}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        hooks.length > 0 && <p className="shk-hint">Select a hook to style it.</p>
      )}
    </div>
  );
}
